import { useEffect, useMemo, useRef, useState } from "react";
import emailjs from "@emailjs/browser";
import {
  Send,
  RefreshCw,
  Mail,
  Phone,
  MapPin,
  Instagram,
  Facebook,
  CheckCircle2,
  AlertCircle,
  ChevronDown,
} from "lucide-react";
import { COMPANY, EMAILJS } from "@/lib/sps-content";
import { useRevealOnScroll } from "@/hooks/use-reveal-on-scroll";

const buildCaptcha = () => {
  const a = Math.floor(Math.random() * 8) + 2;
  const b = Math.floor(Math.random() * 8) + 1;
  return { a, b, answer: a + b };
};

const initialForm = {
  nombre: "",
  empresa: "",
  correo: "",
  telefono: "",
  pais: "",
  servicio: "",
  etapa: "",
  proyecto: "",
  volumen: "",
  mercado: "",
  marca: "",
};

const OPTIONS = {
  servicio: [
    "SPS Distilling",
    "SPS Bottling",
    "Distilling + Bottling",
    "Otro",
  ],
  etapa: [
    "Idea inicial",
    "Desarrollo de producto",
    "Producción piloto",
    "Listo para escalar",
    "Marca actualmente en el mercado",
  ],
  volumen: [
    "Menos de 1,000 unidades",
    "1,000 - 5,000 unidades",
    "5,000 - 20,000 unidades",
    "Más de 20,000 unidades",
    "Aún no lo sé",
  ],
  mercado: ["México", "Exportación", "Ambos", "Otro"],
  marca: ["Sí", "No", "En proceso", "Necesito orientación"],
};

export const Contact = () => {
  useRevealOnScroll();
  const formRef = useRef(null);
  const [form, setForm] = useState(initialForm);
  const [captcha, setCaptcha] = useState(buildCaptcha());
  const [captchaInput, setCaptchaInput] = useState("");
  const [status, setStatus] = useState({ state: "idle", msg: "" });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    try {
      emailjs.init({ publicKey: EMAILJS.publicKey });
    } catch (err) {
      console.warn("EmailJS init failed", err);
    }
  }, []);

  const captchaValid = useMemo(
    () => captchaInput.trim() !== "" && Number(captchaInput) === captcha.answer,
    [captchaInput, captcha],
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    if (errors[name]) setErrors((er) => ({ ...er, [name]: "" }));
  };

  const validate = () => {
    const e = {};
    if (!form.nombre.trim()) e.nombre = "Requerido";
    if (!form.correo.trim()) e.correo = "Requerido";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.correo)) e.correo = "Correo inválido";
    if (!form.telefono.trim()) e.telefono = "Requerido";
    if (!form.pais.trim()) e.pais = "Requerido";
    if (!form.servicio.trim()) e.servicio = "Requerido";
    if (!form.volumen.trim()) e.volumen = "Requerido";
    if (!captchaValid) e.captcha = "Resuelve el captcha";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const refreshCaptcha = () => {
    setCaptcha(buildCaptcha());
    setCaptchaInput("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setStatus({ state: "sending", msg: "" });
    try {
      await emailjs.send(
        EMAILJS.serviceId,
        EMAILJS.templateId,
        {
          nombre: form.nombre,
          empresa: form.empresa,
          correo: form.correo,
          telefono: form.telefono,
          pais: form.pais,
          servicio: form.servicio,
          etapa: form.etapa,
          proyecto: form.proyecto,
          volumen: form.volumen,
          mercado: form.mercado,
          marca_registrada: form.marca,
          mensaje: form.proyecto,
          from_name: form.nombre,
          from_email: form.correo,
          reply_to: form.correo,
          phone: form.telefono,
          company: form.empresa,
          message: form.proyecto,
        },
        { publicKey: EMAILJS.publicKey },
      );
      setStatus({
        state: "success",
        msg: "¡Solicitud enviada! Te contactaremos pronto.",
      });
      setForm(initialForm);
      refreshCaptcha();
    } catch (err) {
      setStatus({
        state: "error",
        msg: "No pudimos enviar la solicitud. Inténtalo de nuevo en unos segundos.",
      });
    }
  };

  const inputBase =
    "w-full bg-transparent border-b px-0 py-3 text-white placeholder:text-[#8f9194]/50 focus:outline-none focus:ring-0 transition-colors font-sans-modern font-light";

  return (
    <section
      id="contacto"
      data-testid="contact-section"
      className="relative py-24 md:py-32 lg:py-40 bg-[#001825]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Left intro */}
          <aside className="lg:col-span-5 reveal">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-px w-10 bg-[#8f9194]" />
              <span className="text-[10px] tracking-[0.4em] uppercase text-[#8f9194] font-sans-modern">
                Contacto
              </span>
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl font-light text-white leading-[1.05]">
              Construyamos
              <br />
              juntos
            </h2>
            <p className="mt-8 text-[#d1d5db] text-base font-light leading-relaxed font-sans-modern max-w-md">
              Cuéntanos sobre tu proyecto. Nuestro equipo te responderá para
              definir cómo podemos colaborar contigo.
            </p>

            <ul className="mt-12 space-y-6">
              <li className="flex items-start gap-4" data-testid="contact-info-phone">
                <Phone size={18} strokeWidth={1.5} className="text-[#8f9194] mt-1" />
                <a
                  href={`tel:${COMPANY.phoneHref}`}
                  className="text-white font-sans-modern hover:text-[#b0b3b8] transition-colors"
                >
                  {COMPANY.phone}
                </a>
              </li>
              <li className="flex items-start gap-4" data-testid="contact-info-email">
                <Mail size={18} strokeWidth={1.5} className="text-[#8f9194] mt-1" />
                <a
                  href={`mailto:${COMPANY.email}`}
                  className="text-white font-sans-modern hover:text-[#b0b3b8] transition-colors break-all"
                >
                  {COMPANY.email}
                </a>
              </li>
              <li className="flex items-start gap-4" data-testid="contact-info-address">
                <MapPin size={18} strokeWidth={1.5} className="text-[#8f9194] mt-1" />
                <span className="text-white font-sans-modern">{COMPANY.address}</span>
              </li>
            </ul>

            <div className="mt-10 flex gap-3">
              <a
                href={COMPANY.instagram}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-social-instagram"
                aria-label="Instagram"
                className="h-11 w-11 border border-[#8f9194]/40 flex items-center justify-center text-[#b0b3b8] hover:bg-[#8f9194] hover:text-[#001825] hover:border-[#8f9194] transition-colors"
              >
                <Instagram size={16} strokeWidth={1.5} />
              </a>
              <a
                href={COMPANY.facebook}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-social-facebook"
                aria-label="Facebook"
                className="h-11 w-11 border border-[#8f9194]/40 flex items-center justify-center text-[#b0b3b8] hover:bg-[#8f9194] hover:text-[#001825] hover:border-[#8f9194] transition-colors"
              >
                <Facebook size={16} strokeWidth={1.5} />
              </a>
            </div>
          </aside>

          {/* Form */}
          <div className="lg:col-span-7 reveal" style={{ transitionDelay: "120ms" }}>
            <form
              ref={formRef}
              onSubmit={handleSubmit}
              data-testid="contact-form"
              className="bg-[#002438] border border-[#8f9194]/15 p-8 md:p-12 space-y-7"
              noValidate
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
                <TextField
                  label="Nombre"
                  name="nombre"
                  required
                  value={form.nombre}
                  onChange={handleChange}
                  error={errors.nombre}
                  inputClass={inputBase}
                  placeholder="Tu nombre"
                />
                <TextField
                  label="Empresa"
                  name="empresa"
                  value={form.empresa}
                  onChange={handleChange}
                  error={errors.empresa}
                  inputClass={inputBase}
                  placeholder="Nombre de la empresa"
                />
                <TextField
                  label="Correo electrónico"
                  name="correo"
                  type="email"
                  required
                  value={form.correo}
                  onChange={handleChange}
                  error={errors.correo}
                  inputClass={inputBase}
                  placeholder="tu@empresa.com"
                />
                <TextField
                  label="Teléfono"
                  name="telefono"
                  type="tel"
                  required
                  value={form.telefono}
                  onChange={handleChange}
                  error={errors.telefono}
                  inputClass={inputBase}
                  placeholder="+52 ..."
                />
                <TextField
                  label="País"
                  name="pais"
                  required
                  value={form.pais}
                  onChange={handleChange}
                  error={errors.pais}
                  inputClass={inputBase}
                  placeholder="México, USA, ..."
                />
              </div>

              <SelectField
                label="¿Qué servicio te interesa?"
                name="servicio"
                required
                value={form.servicio}
                onChange={handleChange}
                options={OPTIONS.servicio}
                error={errors.servicio}
                inputClass={inputBase}
              />

              <SelectField
                label="¿En qué etapa se encuentra tu proyecto?"
                name="etapa"
                value={form.etapa}
                onChange={handleChange}
                options={OPTIONS.etapa}
                error={errors.etapa}
                inputClass={inputBase}
              />

              <div>
                <label className="block text-[10px] tracking-[0.3em] uppercase text-[#8f9194] mb-3 font-sans-modern">
                  Cuéntanos sobre tu proyecto
                </label>
                <textarea
                  data-testid="contact-input-proyecto"
                  name="proyecto"
                  rows="4"
                  value={form.proyecto}
                  onChange={handleChange}
                  placeholder="Comparte con nosotros los detalles de tu proyecto..."
                  className={`${inputBase} border-[#8f9194]/30 focus:border-[#b0b3b8] resize-none`}
                />
              </div>

              <SelectField
                label="Volumen estimado de producción"
                name="volumen"
                required
                value={form.volumen}
                onChange={handleChange}
                options={OPTIONS.volumen}
                error={errors.volumen}
                inputClass={inputBase}
              />

              <SelectField
                label="Mercado objetivo"
                name="mercado"
                value={form.mercado}
                onChange={handleChange}
                options={OPTIONS.mercado}
                error={errors.mercado}
                inputClass={inputBase}
              />

              <SelectField
                label="¿Ya cuenta con marca registrada?"
                name="marca"
                value={form.marca}
                onChange={handleChange}
                options={OPTIONS.marca}
                error={errors.marca}
                inputClass={inputBase}
              />

              {/* Captcha */}
              <div className="border-t border-[#8f9194]/15 pt-7">
                <label className="block text-[10px] tracking-[0.3em] uppercase text-[#8f9194] mb-3 font-sans-modern">
                  Verificación de seguridad
                </label>
                <div className="flex flex-wrap items-center gap-4">
                  <div
                    data-testid="captcha-question"
                    className="px-5 py-3 bg-[#001825] border border-[#8f9194]/30 font-serif-display text-xl text-white tracking-wide select-none"
                  >
                    {captcha.a} + {captcha.b} = ?
                  </div>
                  <input
                    data-testid="captcha-input"
                    type="number"
                    inputMode="numeric"
                    value={captchaInput}
                    onChange={(e) => setCaptchaInput(e.target.value)}
                    placeholder="Tu respuesta"
                    className={`${inputBase} border-[#8f9194]/30 focus:border-[#b0b3b8] max-w-[160px]`}
                  />
                  <button
                    type="button"
                    data-testid="captcha-refresh"
                    onClick={refreshCaptcha}
                    className="inline-flex items-center gap-2 text-xs text-[#8f9194] hover:text-white transition-colors"
                  >
                    <RefreshCw size={14} strokeWidth={1.5} />
                    Cambiar
                  </button>
                  {captchaInput && (
                    <span
                      data-testid="captcha-status"
                      className={`text-xs font-sans-modern ${
                        captchaValid ? "text-emerald-300" : "text-red-300"
                      }`}
                    >
                      {captchaValid ? "Verificado" : "Respuesta incorrecta"}
                    </span>
                  )}
                </div>
                {errors.captcha && (
                  <p data-testid="error-captcha" className="mt-2 text-xs text-red-300">
                    {errors.captcha}
                  </p>
                )}
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2">
                <p className="text-[11px] text-[#8f9194] font-sans-modern">
                  Al enviar aceptas que SPS use tus datos para responder a tu solicitud.
                </p>
                <button
                  type="submit"
                  data-testid="contact-submit-button"
                  disabled={status.state === "sending"}
                  className="group inline-flex items-center justify-center gap-3 px-8 py-4 bg-[#f8f9fa] text-[#001825] text-xs uppercase tracking-[0.25em] font-medium hover:bg-[#8f9194] hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {status.state === "sending" ? "Enviando..." : "Enviar solicitud"}
                  <Send size={14} strokeWidth={1.5} className="transition-transform group-hover:translate-x-1" />
                </button>
              </div>

              {status.state === "success" && (
                <div
                  data-testid="contact-success"
                  className="flex items-start gap-3 p-4 border border-emerald-400/30 bg-emerald-400/5 text-emerald-200"
                >
                  <CheckCircle2 size={18} strokeWidth={1.5} className="mt-0.5 flex-shrink-0" />
                  <p className="text-sm font-sans-modern">{status.msg}</p>
                </div>
              )}
              {status.state === "error" && (
                <div
                  data-testid="contact-error"
                  className="flex items-start gap-3 p-4 border border-red-400/30 bg-red-400/5 text-red-200"
                >
                  <AlertCircle size={18} strokeWidth={1.5} className="mt-0.5 flex-shrink-0" />
                  <p className="text-sm font-sans-modern">{status.msg}</p>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

const TextField = ({ label, name, type = "text", value, onChange, placeholder, error, inputClass, required }) => (
  <div>
    <label className="block text-[10px] tracking-[0.3em] uppercase text-[#8f9194] mb-3 font-sans-modern">
      {label}
      {required && <span className="text-red-300 ml-1">*</span>}
    </label>
    <input
      data-testid={`contact-input-${name}`}
      name={name}
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className={`${inputClass} border-[#8f9194]/30 focus:border-[#b0b3b8]`}
    />
    {error && (
      <p data-testid={`error-${name}`} className="mt-2 text-xs text-red-300 font-sans-modern">
        {error}
      </p>
    )}
  </div>
);

const SelectField = ({ label, name, value, onChange, options, error, inputClass, required }) => (
  <div>
    <label className="block text-[10px] tracking-[0.3em] uppercase text-[#8f9194] mb-3 font-sans-modern">
      {label}
      {required && <span className="text-red-300 ml-1">*</span>}
    </label>
    <div className="relative">
      <select
        data-testid={`contact-select-${name}`}
        name={name}
        value={value}
        onChange={onChange}
        className={`${inputClass} border-[#8f9194]/30 focus:border-[#b0b3b8] appearance-none pr-10 cursor-pointer ${
          !value ? "text-[#8f9194]/60" : ""
        }`}
      >
        <option value="" disabled className="bg-[#001825] text-[#8f9194]/60">
          Selecciona una opción
        </option>
        {options.map((opt) => (
          <option key={opt} value={opt} className="bg-[#001825] text-white">
            {opt}
          </option>
        ))}
      </select>
      <ChevronDown
        size={16}
        strokeWidth={1.5}
        className="absolute right-0 top-1/2 -translate-y-1/2 text-[#8f9194] pointer-events-none"
      />
    </div>
    {error && (
      <p data-testid={`error-${name}`} className="mt-2 text-xs text-red-300 font-sans-modern">
        {error}
      </p>
    )}
  </div>
);
